import type { LandingPageContent } from "@brookbush/types";

import type { GetStaticProps } from "next";
import { useRouter } from "next/compat/router";
import { type PropsWithChildren, useEffect, useState } from "react";

import Facts from "../../components/Facts";
import HomeExplorer from "../components/HomeExplorer";
import LandingPageAccreditations from "../info/components/LandingPageAccreditations";
import LandingPageTestimonials from "../info/components/LandingPageTestimonials";
import TrialHero from "./components/TrialHero";
import Faqs from "~/components/Faqs";
import FeatureCards from "~/components/FeatureCards";
import Footer from "~/components/Footer";
import FullWidthCTA from "~/components/FullWidthCTA";
import Navbar from "~/components/Navbar";
import { useAccess } from "~/hooks";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const TrialMembership: NextPageWithProps<{
	landingPageContent: LandingPageContent;
}> = ({ landingPageContent }) => {
	const features = [
		{
			title: "Acquire an Advanced Credential & CECs/CEUs at the same time!",
			description:
				"Every course you take in pursuit of certification has also been pre-approved for continuing education credits.",
			links: [
				{
					href: "/articles/easy-continuing-education-credits-accreditation",
					label: "CEC Approvals",
				},
			],
			thumbnailUrl:
				"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/home-why-1.jpeg",
		},
		{
			title: "Best Reviewed Certification and Course Provider",
			description:
				"We are the highest-rated Certification and course provider with 100s of Google Reviews and an average rating of 5 out of 5 stars!",
			links: [
				{
					href: "https://www.google.com/maps/place/Brookbush+Institute/@40.7470886,-73.9793827,17z/data=!3m1!5s0x89c259042bc3f44d:0xcfda66d9ec348ef6!4m8!3m7!1s0x89c2599677c1a285:0x806ee51940b04b1d!8m2!3d40.7470846!4d-73.9768078!9m1!1b1!16s%2Fg%2F11pck_nqml?entry=ttu",
					label: "Google Reviews",
				},
			],
			thumbnailUrl:
				"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/home-why-2.jpeg",
		},
		{
			title: "Monthly Membership, cancel anytime",
			description:
				"The only true monthly membership in the industry, cancel anytime, all courses, features, and study aides included (no additional fees).",
			links: [
				{
					href: "/articles/Cost-of-Personal-Training-Certification",
					label: "Compare Cost",
				},
			],
			thumbnailUrl:
				"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/home-why-3.jpeg",
		},
		{
			title: "Better Certifications by Design",
			description:
				"Check out these articles for more on how we are re-inventing certifications in the industry:",
			links: [
				{
					href: "/articles/best-personal-trainer-certification",
					label: "Certified Personal Trainer",
				},
				{
					href: "/articles/better-corrective-exercise-certification",
					label: "Human Movement Specialist",
				},
				{
					href: "/articles/comparing-cost-manual-therapy-certifications",
					label: "Integrated Manual Therapist",
				},
			],
			thumbnailUrl:
				"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/home-why-4.png",
		},
	];

	const [isFirstRender, setIsFirstRender] = useState(true);

	const router = useRouter();

	const { isMember, isFormerMember, isLoadingAccess } = useAccess();

	useEffect(() => {
		if (isFirstRender && !isLoadingAccess) {
			setIsFirstRender(false);
			(isMember || isFormerMember) && router?.replace("/");
		}
	}, [isFirstRender, isFormerMember, isLoadingAccess, isMember, router]);

	if (isMember || isFormerMember) return null;

	return (
		<main>
			<Navbar isHomePage />

			<TrialHero
				imageUrl={landingPageContent.thumbnail.url}
				title={landingPageContent.hero.title}
				subtitle={landingPageContent.hero.description}
			/>

			<Facts />

			<FeatureCards
				features={features}
				title="Why Choose Brookbush Institute"
			/>

			<HomeExplorer />

			<LandingPageAccreditations
				accreditations={landingPageContent.accreditations}
			/>
			{/*
        <LandingPageModules
          certification={landingPageContent.linkedCertification as any}
        /> */}

			<LandingPageTestimonials testimonials={landingPageContent.testimonials} />

			{landingPageContent.faqs && <Faqs faqs={landingPageContent.faqs} />}

			<FullWidthCTA />

			<Footer />
		</main>
	);
};

// eslint-disable-next-line react/display-name
TrialMembership.Layout = ({ children }: PropsWithChildren<{}>) => (
	<>{children}</>
);

export const getStaticProps: GetStaticProps = async () => {
	try {
		const landingPageContent = await fetcher(`/landing-pages/trial-membership`);

		if (!landingPageContent) {
			return {
				notFound: true,
			};
		}

		return {
			props: {
				landingPageContent,
			},
		};
	} catch (_error) {
		return {
			props: {
				error: "Failed to load data",
			},
		};
	}
};

export default TrialMembership;
